/**
 * A single-series time trend — an amount per date, as a filled line. The one
 * chart form here that's genuinely temporal rather than a snapshot bucketed
 * by severity, so it gets what the skill calls out as the default for
 * line/area: a hover crosshair and per-point tooltip, not just a static
 * shape. One series needs no legend — the widget's own title names it.
 *
 * Plain SVG with index-based x-spacing (points are assumed evenly spaced —
 * true here, since the caller always emits one point per bucket in the
 * range, zero-filled). `onPointerMove` finds the nearest point by dividing
 * the pointer's x by the column width; no date-scale math needed because the
 * spacing is already uniform.
 */

import { useState } from 'react';

export interface TrendPoint {
  label: string;
  value: number;
  displayValue: string;
}

const WIDTH = 640;
const HEIGHT = 180;
const PAD_LEFT = 44;
const PAD_RIGHT = 12;
const PAD_TOP = 12;
const PAD_BOTTOM = 24;

export function TrendChart({
  points,
  colorVar = '--accent',
}: {
  points: TrendPoint[];
  colorVar?: string;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const plotWidth = WIDTH - PAD_LEFT - PAD_RIGHT;
  const plotHeight = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const max = Math.max(1, ...points.map((p) => p.value));

  const xAt = (i: number) =>
    PAD_LEFT + (points.length <= 1 ? plotWidth / 2 : (i / (points.length - 1)) * plotWidth);
  const yAt = (v: number) => PAD_TOP + plotHeight - (v / max) * plotHeight;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.value)}`)
    .join(' ');
  const areaPath =
    points.length > 0
      ? `${linePath} L ${xAt(points.length - 1)} ${PAD_TOP + plotHeight} L ${xAt(0)} ${PAD_TOP + plotHeight} Z`
      : '';

  const gridLines = [0, 0.5, 1];

  function handleMove(event: React.PointerEvent<SVGSVGElement>) {
    if (points.length === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const relX = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const ratio = points.length <= 1 ? 0 : (relX - PAD_LEFT) / plotWidth;
    const index = Math.round(ratio * (points.length - 1));
    setHoverIndex(Math.max(0, Math.min(points.length - 1, index)));
  }

  if (points.length === 0) {
    return <p className="muted">No settled deposits in this window.</p>;
  }

  const hover = hoverIndex !== null ? points[hoverIndex] : null;

  return (
    <div className="trend-chart">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        role="img"
        aria-label="Deposit money collected over time"
        onPointerMove={handleMove}
        onPointerLeave={() => setHoverIndex(null)}
      >
        {gridLines.map((t) => (
          <line
            key={t}
            x1={PAD_LEFT}
            x2={WIDTH - PAD_RIGHT}
            y1={PAD_TOP + plotHeight * (1 - t)}
            y2={PAD_TOP + plotHeight * (1 - t)}
            stroke="var(--line)"
            strokeWidth={1}
          />
        ))}
        <text x={4} y={PAD_TOP + plotHeight + 4} className="trend-chart__axis-label">
          $0
        </text>
        <text x={4} y={PAD_TOP + 8} className="trend-chart__axis-label">
          {Math.round(max).toLocaleString('en-US')}
        </text>

        <path
          d={areaPath}
          fill={`color-mix(in srgb, var(${colorVar}) 18%, transparent)`}
          stroke="none"
        />
        <path
          d={linePath}
          fill="none"
          stroke={`var(${colorVar})`}
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {hoverIndex !== null ? (
          <>
            <line
              x1={xAt(hoverIndex)}
              x2={xAt(hoverIndex)}
              y1={PAD_TOP}
              y2={PAD_TOP + plotHeight}
              stroke="var(--muted)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <circle
              cx={xAt(hoverIndex)}
              cy={yAt(points[hoverIndex]!.value)}
              r={4}
              fill={`var(${colorVar})`}
              stroke="var(--surface)"
              strokeWidth={2}
            />
          </>
        ) : null}

        <text x={PAD_LEFT} y={HEIGHT - 4} className="trend-chart__axis-label">
          {points[0]!.label}
        </text>
        <text
          x={WIDTH - PAD_RIGHT}
          y={HEIGHT - 4}
          textAnchor="end"
          className="trend-chart__axis-label"
        >
          {points[points.length - 1]!.label}
        </text>
      </svg>
      <div className="trend-chart__tooltip" aria-live="polite">
        {hover ? (
          <span>
            <strong>{hover.label}</strong> — {hover.displayValue}
          </span>
        ) : (
          <span className="muted">Hover the chart for a day's total.</span>
        )}
      </div>
    </div>
  );
}
